import { ComponentClass, ComponentType, createElement, PureComponent } from 'react';
import { BehaviorSubject, merge, Observable, SchedulerLike, Subscription } from 'rxjs';
import { map, observeOn } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
const hoistNonReactStatics = require('hoist-non-react-statics'); // tslint:disable-line no-require-imports no-var-requires

export type Observify<P extends object> = {
	readonly [K in keyof P]: Observable<P[K]>;
};

/**
 * Selector function - takes Component properties as Observable and return WithRXSelectorResult
 */
type WithRXSelector<P extends object, D extends Partial<P>> = (
	props$: Observable<Readonly<P>>,
) => WithRXSelectorResult<P, D>;

/**
 * Result of Selector function - contains default value for properties, property Observables and effects
 * This result access to
 */
export type WithRXSelectorResult<P extends object, D extends Partial<P>> = {
	/**
	 * Object of Observable properties.
	 * Each event in these Observables will rerender component and pass values from Observable to component
	 * When component will be mount, these we subscribe to these observables and unsubscribe when component will be destroyed.
	 * So you don't care about manage subscriptions manually.
	 *
	 * Also these properties in the selector clojure - so you can access to original properties from Component
	 */
	props?: Partial<Observify<P>>;
	/**
	 * Default component props, these properties pass to component when Observable property is not pushed yet.
	 * These properties don't change default props in component, just was passed directly and compose with value from props Observable,
	 * so it's safe to pass many time different default props for the same component, instead of withDefaults.
	 *
	 * Also these properties in the selector clojure - so you can access to original properties from Component
	 */
	defaultProps?: D;
	/**
	 * When you need to subscribe and unsubscribe to Observable when component is present, you can use this effects
	 * When component will mount, it subscribe to effects$ and unsubscribe when destroyed.
	 * So you don't care about manage subscriptions manually.
	 */
	effects$?: Observable<unknown>;
};

export type WithRXOptions = {
	scheduler?: SchedulerLike;
};

export const withRxJs =
	<P extends object>(Target: ComponentType<P>) =>
	<D extends Partial<P>>(
		selector: WithRXSelector<P, D>,
		options: WithRXOptions = {},
	): ComponentClass<Omit<P, keyof D> & Partial<P>> => {
		class WithRX extends PureComponent<P, Partial<P>> {
			static displayName = `WithRxJs(${Target.displayName || Target.name})`;

			private readonly props$ = new BehaviorSubject<P>(this.props);
			private readonly selected = selector(this.props$.asObservable());
			private inputSubscription?: Subscription;
			private effectsSubscription?: Subscription;

			componentDidMount() {
				const { props, effects$ } = this.selected;
				if (props) {
					const inputs: Observable<Partial<P>>[] = Object.keys(props).map(key =>
						// @ts-ignore
						props[key].pipe(map(value => ({ [key]: value }))),
					);
					const merged = merge(...inputs);
					const result = options.scheduler ? merged.pipe(observeOn(options.scheduler)) : merged;
					this.inputSubscription = result.subscribe(this.setState.bind(this));
				}
				if (effects$) {
					this.effectsSubscription = effects$.subscribe();
				}
			}

			componentWillReceiveProps(props: P) {
				this.props$.next(props);
			}

			componentWillUnmount() {
				this.inputSubscription && this.inputSubscription.unsubscribe();
				this.effectsSubscription && this.effectsSubscription.unsubscribe();
			}

			render() {
				return createElement(
					Target,
					Object.assign({}, this.selected.defaultProps, this.props, this.state || {}),
				);
			}
		}

		hoistNonReactStatics(WithRX, Target);

		return WithRX as any; // defaultProps are tracked by defaultProps argument;
	};
